'use client';

import React, { HTMLAttributes, Profiler } from 'react';

import { Locale } from '../../../i18n.config';
import Image from 'next/image';
import parser from 'html-react-parser';
import { getTranslation } from '@/lib/utils';
import { parseOptions } from '@/utils/parseOptions';
import { MapPinButton } from '../MapPinButton';
import Slider from 'react-slick';
import './styles.css';
import QualityIcon from '../Icons/Quality';
import getMediaFilterUrl from '@/utils/get-media-filter';
import { useLocale } from '@/hooks/useLocale';

interface ICarouselItemProps extends HTMLAttributes<HTMLDivElement> {
  lang: Locale;
  itemData: GuideItem;
  noHalfImage?: boolean;
  isCarouselOutside?: boolean;
}

interface IMultiCarouselSectionProps extends HTMLAttributes<HTMLDivElement> {
  noHalfImage?: boolean;
  sectionData: Section;
}

const CarouselItem: React.FC<ICarouselItemProps> = ({
  itemData,
  lang,
  noHalfImage,
  isCarouselOutside = false,
}) => {
  const itemInfo = itemData.surrounding?.id ? itemData.surrounding : itemData;
  const { image } = itemData;
  const halfWidth = image?.filename.slice(-4) === '.png';
  const translate =
    (itemInfo as GuideItem).guideItemTranslation ||
    (itemInfo as Surrounding).translate;
  const translation = getTranslation(translate || [], lang);

  const bannerUrl =
    getMediaFilterUrl({
      mediaFilters: itemInfo?.image?.mediaFilters || [],
      filterName: 'medium_1-1_webp',
    }) ??
    getMediaFilterUrl({
      mediaFilters: itemInfo?.image?.mediaFilters || [],
      filterName: 'small_1-1_webp',
    });

  return (
    <div className="w-full group-[.is-outside-carousel]:w-1/4 px-[5px]">
      <div
        className={`flex relative box-border flex-col items-center w-4/5 mx-auto xl:w-full`}>
        {itemData.surrounding && <MapPinButton guideItem={itemData} />}
        {itemInfo.image ? (
          <div
            className={`w-full aspect-square overflow-hidden tablet:p-4 relative mb-3 ${
              halfWidth && '!w-1/3'
            }`}>
            <Image
              src={
                bannerUrl ??
                `${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`
              }
              alt={translation?.title}
              fill
              quality={30}
              className="object-cover absolute -z-10"
              sizes="(min-width: 640px) 50vw, (min-width: 800px) 33vw, (min-width: 1200px) 25vw, 80vw"
            />
          </div>
        ) : (
          <QualityIcon className="mb-8" />
        )}
        <div>
          <h4 className="mb-3 text-subtitle font-text font-bold whitespace-normal break-words text-gray-900 text-center">
            {translation?.title}
          </h4>
          <div className="text-base whitespace-normal break-words text-gray-900 text-center">
            {parser(translation?.description || '', parseOptions)}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MultiCarouselSection: React.FC<IMultiCarouselSectionProps> = ({
  sectionData,
  noHalfImage = false,
  ...rest
}) => {
  const lang = useLocale();

  const sectionItems = sectionData?.guideItems || [];
  const noHalfWidth = sectionItems.some((item) =>
    item.image ? item.image.type === 'cover' : true,
  );

  const items = sectionItems.map((item) => (
    <CarouselItem
      itemData={item}
      noHalfImage={noHalfWidth}
      lang={lang}
      key={item.id}
    />
  ));

  const itemsAmount = sectionData?.guideItems?.length || 0;
  const settings = {
    dots: false,
    infinite: items.length > 4,
    swipe: true,
    speed: 1000,
    arrows: items.length > 4,
    initialSlide: 0,
    slidesToShow: 4,
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          infinite: items.length > 3,
          arrows: items.length > 3,
          swipe: true,
          slidesToShow: items.length > 3 ? 3 : items.length,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          infinite: items.length > 3,
          arrows: items.length > 3,
          swipe: true,
          slidesToShow: items.length > 3 ? 3 : items.length,
        },
      },
      {
        breakpoint: 1100,
        settings: {
          infinite: items.length > 2,
          arrows: items.length > 2,
          swipe: true,
          slidesToShow: items.length > 2 ? 2 : items.length,
          slidesToScroll: items.length > 2 ? 2 : items.length,
        },
      },
      {
        breakpoint: 750,
        settings: {
          infinite: true,
          arrows: true,
          swipe: true,
          slidesToShow: 1,
        },
      },
    ],
    slidesToScroll: 1,
  };

  const translation = getTranslation(
    sectionData?.guideSectionTranslation || [],
    lang,
  );

  return (
    <Profiler id="MultiCarousel" onRender={() => {}}>
      <section
        className={`
      flex flex-col w-full max-w-[1680px] mx-auto px-4 pt-[40px] tablet:px-10 mb-[50px] desktop:mb-[150px] desktop:px-20 z-10 items-center group
      carousel-${itemsAmount}-items
      `}
        {...rest}>
        <div className=" w-full px-0 mb-[60px] flex flex-col justify-center">
          <h2 className="mb-6 text-title font-title font-bold text-center">
            {translation?.title}
          </h2>
          <div className="text-gray-850 text-lg leading-[1.5] text-center">
            {parser(translation?.description || '', parseOptions)}
          </div>
        </div>

        <div
          className={`
            hidden w-full group is-outside-carousel flex-row desktop:mt-16 h-auto desktop:mb-8 desktop:!m-0 justify-center
            group-[.carousel-0-items]:hidden
            group-[.carousel-2-items]:tablet:flex
            group-[.carousel-3-items]:desktop:flex
            group-[.carousel-4-items]:xl:flex
            `}>
          {items}
        </div>
        <Slider
          {...settings}
          className="
            tour-carousel multi-carousel
            group-[.carousel-0-items]:hidden 
            group-[.carousel-2-items]:tablet:hidden 
            group-[.carousel-3-items]:desktop:hidden 
            group-[.carousel-4-items]:xl:hidden 
            ">
          {items}
        </Slider>
      </section>
    </Profiler>
  );
};

export default MultiCarouselSection;
